import { BACKEND_URL } from "@/lib/api";
import React, { useState, useEffect } from "react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Loader2, ArrowRight, ShieldCheck, Sparkles } from "lucide-react";
import { toast } from "sonner";
import axios from "axios";

const API = `${BACKEND_URL}/api`;

/**
 * Reserve Campaign — simple 2-step modal. No deposit / no payment up front.
 *
 * Step 1: Review configured order
 * Step 2: Buyer details (company, name, email, password? if new)
 *
 * On submit → backend persists reservation as `pending_review`, returns JWT.
 * Buyer is signed in immediately and redirected to /reserve-success.
 * Compliance team approves within 24h → buyer is prompted to top up wallet to start receiving leads.
 */
export default function ReserveCampaignModal({ open, onOpenChange, order, estimate, tierMeta }) {
  const [step, setStep] = useState(1);
  const [form, setForm] = useState({ company: "", name: "", email: "", password: "" });
  const [busy, setBusy] = useState(false);
  const [errors, setErrors] = useState({});
  const [emailCheck, setEmailCheck] = useState({ checked: false, exists: false, role: null });

  const reset = () => {
    setStep(1);
    setForm({ company: "", name: "", email: "", password: "" });
    setErrors({});
    setEmailCheck({ checked: false, exists: false, role: null });
  };

  useEffect(() => {
    if (!form.email.match(/^[^\s@]+@[^\s@]+\.[^\s@]+$/)) {
      setEmailCheck({ checked: false, exists: false, role: null });
      return;
    }
    const t = setTimeout(async () => {
      try {
        const { data } = await axios.post(`${API}/auth/check-email`, { email: form.email.trim().toLowerCase() });
        setEmailCheck({ checked: true, exists: !!data.exists, role: data.role });
      } catch (_) {
        setEmailCheck({ checked: false, exists: false, role: null });
      }
    }, 350);
    return () => clearTimeout(t);
  }, [form.email]);

  const validate = () => {
    const e = {};
    if (!form.company.trim()) e.company = "Required";
    if (!form.name.trim()) e.name = "Required";
    if (!form.email.match(/^[^\s@]+@[^\s@]+\.[^\s@]+$/)) e.email = "Valid email required";
    if (emailCheck.exists && emailCheck.role && emailCheck.role !== "client") {
      e.email = `An account with this email exists as ${emailCheck.role}. Use a different email.`;
    }
    if (!emailCheck.exists) {
      if (!form.password) e.password = "Min 8 characters — used to sign in to your buyer dashboard";
      else if (form.password.length < 8) e.password = "Min 8 characters";
    }
    setErrors(e);
    return Object.keys(e).length === 0;
  };

  const submit = async () => {
    if (!validate()) return;
    setBusy(true);
    try {
      const payload = {
        sector: order.sector,
        acquisition_type: order.acquisition_type,
        exclusivity: order.exclusivity,
        delivery_method: order.delivery_method,
        volume: order.volume,
        tier: order.tier,
        estimate_total: estimate?.total || 0,
        company: form.company.trim(),
        name: form.name.trim(),
        email: form.email.trim().toLowerCase(),
      };
      if (!emailCheck.exists && form.password) payload.password = form.password;
      const { data } = await axios.post(`${API}/orders/reserve`, payload);
      if (data.token) localStorage.setItem("connexis_token", data.token);
      if (data.user) localStorage.setItem("connexis_user", JSON.stringify(data.user));
      window.location.href = `/reserve-success?reservation_id=${data.reservation_id}`;
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not submit reservation. Please try again.");
      setBusy(false);
    }
  };

  const handleOpenChange = (v) => {
    if (!v) reset();
    onOpenChange(v);
  };

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent className="max-w-lg" data-testid="reserve-campaign-modal">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-xl">
            <ShieldCheck className="size-5 text-[#0055FF]" /> Reserve your campaign
          </DialogTitle>
          <DialogDescription>
            {step === 1
              ? "Review your configuration. No payment now — we'll review compliance within 24h, then prompt you to top up your wallet."
              : "Tell us who's running this campaign — we'll create your buyer account in 60 seconds."}
          </DialogDescription>
        </DialogHeader>

        {step === 1 && (
          <div className="space-y-4" data-testid="reserve-step-1">
            <div className="rounded-xl border border-slate-200 bg-slate-50/50 p-4 space-y-2.5 text-sm">
              <Row k="Sector" v={(order.sector || "").replace(/-/g, " ").replace(/\b\w/g, (c) => c.toUpperCase())} />
              <Row k="Acquisition type" v={order.acquisition_type === "exclusive" ? "Exclusive" : (order.acquisition_type || "—")} />
              <Row k="Exclusivity" v={order.exclusivity} />
              <Row k="Delivery" v={order.delivery_method} />
              <Row k="Monthly volume" v={`${order.volume.toLocaleString()} leads`} />
              <Row k="Platform tier" v={tierMeta?.name ? `${tierMeta.name}${tierMeta.monthly_gbp ? ` · £${tierMeta.monthly_gbp.toLocaleString()}/mo` : ""}` : "—"} />
              <div className="border-t border-slate-200 pt-2.5 flex items-center justify-between font-medium">
                <span className="text-slate-700">Estimated per-lead monthly</span>
                <span className="text-slate-900 tabular-nums">£{(estimate?.total || 0).toLocaleString()}</span>
              </div>
            </div>

            <div className="flex items-start gap-3 rounded-xl border border-slate-200 p-4">
              <span className="inline-grid place-items-center size-9 rounded-lg bg-[#0F172A] text-white shrink-0">
                <Sparkles className="size-4" strokeWidth={1.6} />
              </span>
              <div className="leading-snug">
                <div className="font-medium text-slate-900 text-sm">No payment up-front · prepaid wallet model</div>
                <p className="text-xs text-slate-600 mt-1">
                  Reserve is free. Once we approve compliance, you top up your buyer wallet (Stripe or bank transfer)
                  and we debit per-lead as they're accepted. Pause anytime.
                </p>
              </div>
            </div>
          </div>
        )}

        {step === 2 && (
          <div className="space-y-4" data-testid="reserve-step-2">
            <div className="grid sm:grid-cols-2 gap-3">
              <div>
                <Label className="text-xs font-medium text-slate-600 mb-1.5 block">Company</Label>
                <Input value={form.company} onChange={(e) => setForm({ ...form, company: e.target.value })} placeholder="Northbridge Mortgage Co." data-testid="reserve-input-company" />
                {errors.company && <p className="text-[11px] text-rose-600 mt-1">{errors.company}</p>}
              </div>
              <div>
                <Label className="text-xs font-medium text-slate-600 mb-1.5 block">Your name</Label>
                <Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder="Jane Doe" data-testid="reserve-input-name" />
                {errors.name && <p className="text-[11px] text-rose-600 mt-1">{errors.name}</p>}
              </div>
            </div>
            <div>
              <Label className="text-xs font-medium text-slate-600 mb-1.5 block">Work email</Label>
              <Input type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} placeholder="jane@northbridge.co.uk" data-testid="reserve-input-email" />
              {errors.email && <p className="text-[11px] text-rose-600 mt-1">{errors.email}</p>}
              {emailCheck.checked && emailCheck.exists && emailCheck.role && emailCheck.role !== "client" && !errors.email && (
                <p className="text-[11px] text-rose-600 mt-1" data-testid="reserve-email-wrong-role">
                  An account exists for this email as <span className="font-mono">{emailCheck.role}</span> — please use a different email.
                </p>
              )}
              {emailCheck.checked && emailCheck.exists && emailCheck.role === "client" && !errors.email && (
                <p className="text-[11px] text-[#0055FF] mt-1" data-testid="reserve-email-existing">
                  ✓ Welcome back — we found your existing buyer account. No password needed.
                </p>
              )}
            </div>
            {!emailCheck.exists && (
              <div data-testid="reserve-password-field">
                <Label className="text-xs font-medium text-slate-600 mb-1.5 block">
                  Create a password
                  <span className="text-slate-400 font-normal"> · used to sign in to your buyer dashboard</span>
                </Label>
                <Input type="password" value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} placeholder="Min 8 characters" data-testid="reserve-input-password" />
                {errors.password && <p className="text-[11px] text-rose-600 mt-1">{errors.password}</p>}
              </div>
            )}
          </div>
        )}

        <DialogFooter className="gap-2 sm:gap-2 sm:justify-between">
          {step === 1 ? (
            <>
              <Button variant="outline" onClick={() => handleOpenChange(false)} className="rounded-full" data-testid="reserve-cancel">
                Cancel
              </Button>
              <Button onClick={() => setStep(2)} className="rounded-full bg-[#0055FF] hover:bg-[#0044CC]" data-testid="reserve-next">
                Continue <ArrowRight className="size-4" />
              </Button>
            </>
          ) : (
            <>
              <Button variant="outline" onClick={() => setStep(1)} className="rounded-full" data-testid="reserve-back">
                Back
              </Button>
              <Button onClick={submit} disabled={busy} className="rounded-full bg-[#0055FF] hover:bg-[#0044CC]" data-testid="reserve-submit">
                {busy ? <><Loader2 className="size-4 animate-spin" /> Submitting…</> : <>Submit reservation <ArrowRight className="size-4" /></>}
              </Button>
            </>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function Row({ k, v }) {
  return (
    <div className="flex items-center justify-between">
      <span className="text-slate-500 text-[12px]">{k}</span>
      <span className="text-slate-900 text-[12.5px] font-medium tabular-nums">{v}</span>
    </div>
  );
}
